import { Link } from "@mui/material";

import { routes } from "@/global/routes";
import {
  DefaultCheckoutTerms,
  DefaultRegistrationTerms,
} from "../../default_terms_and_conditions";
import { faqs, howItWorks } from "./helpers";
import { vendorUUIDs } from "../../constants";

import type { TeamConfig } from "@/global/hooks/useTeamConfig";

import StaticSeatMap from "public/images/altoonacurve/2024-Field-Guide.png";
import FindGamesBanner from "public/images/altoonacurve/Build Your Own Mini Plan Header NEW.png";

const config: TeamConfig = {
  teamSchema: "altoonacurve_flex",
  browserTitle: "Altoona Curve BYO Mini PLan",
  packages: false,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "",
  teamName: "Altoona Curve",

  dynamicPricing: {
    enabled: true,
    showAllDiscounts: true,
    list: [
      {
        id: 1,
        discount: 10,
        minSelectedAmount: 2,
        updatedAccountGroup: 0,
        customProgressBarMessage:
          "Congrats! You've unlocked 10% off your seats! Add 3 more games and receive 15% off, add 2 more games for 25% off or checkout now.",
      },
      {
        id: 2,
        discount: 10,
        minSelectedAmount: 3,
        updatedAccountGroup: 0,
        hideOfferFromModal: true,
        customProgressBarMessage:
          "Add 2 more games for 15% off or 4 more games for 25% off your seats!",
      },
      {
        id: 3,
        discount: 10,
        minSelectedAmount: 4,
        updatedAccountGroup: 0,
        hideOfferFromModal: true,
        customProgressBarMessage:
          "Add 1 more game for 15% off or 3 more games for 25% off your seats!",
      },
      {
        id: 4,
        discount: 15,
        minSelectedAmount: 5,
        updatedAccountGroup: 1,
        customProgressBarMessage:
          "Congrats! You've unlocked 15% off your seats! Add 2 more games and receive 25% off or checkout now.",
      },
      {
        id: 5,
        discount: 15,
        minSelectedAmount: 6,
        updatedAccountGroup: 1,
        hideOfferFromModal: true,
        customProgressBarMessage: "Add 1 more game for 25% off your seats!",
      },
      {
        id: 6,
        discount: 25,
        minSelectedAmount: 7,
        updatedAccountGroup: 2,
        customProgressBarMessage:
          "Congrats! You have unlocked 25% off your seats! Add more games or checkout now.",
      },
    ],
  },

  // seat map configs
  seatMapConfigs: {
    venueId: "",
    mapId: "",
    staticSeatMapImage: StaticSeatMap.src,
    isShowAvailableSeatsButtonHidden: true,
    isView3dButtonHidden: true,
    customSeatMapStaticImageStyle: {
      width: "50%",
      display: "block",
      marginX: "auto",
    },
  },

  // gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: routes.purchaseSeats,
  },

  // purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
  },

  // disabled routes
  disabledRoutes: [routes.giftCard],

  // switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: true,
  inventoryType: "SEAT-LEVEL", // As specified

  // purchase seat configs
  purchaseSeatConfigs: {
    minGameSelectionCount: 2,
    maxGameSelectionCount: 7,
    minSeatLimit: 1,
    isSimilarSeatForAllEvents: true,
    allowManualCartInSimilar: true,
    isCheckoutEditSeatsEnabled: true,
    isDiffSeatAlertEnabled: true,

    hideViewTicketsInThankYouPage: false,
    defaultSeatCount: 1,
    isDisabledEventSelection: false,
    hideAllUpcomingGames: false,
    isRepProduct: false,
  },

  // feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // checkout validations
  checkoutValidation: {
    allowedZipFailCount: 3,
    allowedPaymentFailCount: 3,
  },

  isSaveCardAllowed: true,
  isBrainTreePaymentEnabled: false, // Optional, defaulted to false
  shouldUseInternalLogin: false, // Optional, defaulted to false
  oktaClientId: "", // Not needed for Ticketmaster
  oktaIssuer: "", // Not needed for Ticketmaster
  platform: vendorUUIDs.ticketmaster,
  refreshInventoryWhenEventClick: false,

  // analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "", // No value provided
    attrkPixelId: "",
    beaconId: "",
    metaPixelId: "",
  },

  // redirect configs
  redirectConfigs: {
    internalRedirectUrl: "",
    externalRedirectUrl: "",
  },

  // URLs
  accountManagerUrl: "https://am.ticketmaster.com/altoonacurve/",

  // images
  images: {
    findGamesBannerImage: FindGamesBanner.src,
    authBackgroundImage: FindGamesBanner.src,
    checkoutStatusPageImage: FindGamesBanner.src,
    logoImage:
      "https://ss-leagues.s3.amazonaws.com/Minor%20League%20Baseball/altoona_curve_logo.png" +
      "?width=128&height=75",
    logoNameImage: "",
    faviconImage:
      "https://ss-leagues.s3.amazonaws.com/Minor%20League%20Baseball/altoona_curve_logo.png",
    seatMap3dPreviewImage: "", // No value provided
  },

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },

    featuredGameCard: {
      showLogoWithTitle: false,
      hideOverlayLogo: true,
    },

    bottomBar: {
      showGameSelectionProgress: true,
    },

    progressBar: {
      staticProgressBarText: false,
    },

    customAdaLabel: (
      <>
        To purchase accessible seating locations, please contact the Box Office
        at{" "}
        <Link
          href="tel:8142833108"
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
        >
          (814) 283-3108
        </Link>
        .
      </>
    ),
  },

  colors: {
    primary: "#a23739",
  },

  howItWorks,
  faqs,

  languages: {
    topNavMenuItems: "", // Can be a React node as well
    checkoutTermsAndCondition: <DefaultCheckoutTerms />, // Using standard SeasonShare language
    registrationTermsAndCondition: <DefaultRegistrationTerms />, // Using standard SeasonShare language
    purchaseSeats: {
      similarSeatModalBody: (seatTitle: string) => (
        <>
          We are going to apply the same or similar seats to all games in your
          pack. Seat locations will be visible on the checkout page.
        </>
      ),
      progressBarText:
        "Start by adding 2 games for 10% off, add 5 games for 15% off or add 7 games and get 25% off your seats!",
      progressBarAditionalText: "to continue.",
    },

    checkoutSuccessMessage: "Your purchase is complete!",
    checkoutSuccessDescription: "Your Altoona Curve tickets are confirmed!",

    purchaseGiftCard: null,
    redeemGiftCard: null,

    giftCard: {
      title: "",
      createGiftCard: {
        description: "",
        recipient: {
          sendNowFalse: {
            description: "",
          },
          sendNowTrue: {
            description: "",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description: "",
        },
        logout: {
          description: "",
        },
        success: {
          description: "",
        },
      },
    },

    signInAuthText:
      "Connect your Ticketmaster account to continue with seamless access.",
  },
};

export default config;
